// =============================================================================
// FILE: packages/renderer/src/components/branding/ConnectivityBadge.tsx
// PURPOSE: Displays the current connectivity mode — "Live" (Online) or
//          "Secure Standalone" (offline/air-gapped) — in the TopBar.
//          Clicking opens a compact popover with mode details + force-toggle.
// =============================================================================

import { useRef, useState, useEffect, type JSX } from 'react';
import { Wifi, WifiOff, RefreshCw, ChevronDown, Shield, Globe } from 'lucide-react';
import { useConnectivityMode } from '../../hooks/useConnectivityMode.js';

export function ConnectivityBadge(): JSX.Element {
  const { mode, checking, serverReach, lastChecked, forceMode } = useConnectivityMode();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const isOnline = mode === 'online';

  const ago = lastChecked
    ? Math.round((Date.now() - lastChecked) / 1000) < 60
      ? `${Math.round((Date.now() - lastChecked) / 1000)}s ago`
      : `${Math.round((Date.now() - lastChecked) / 60_000)}m ago`
    : 'checking…';

  return (
    <div ref={ref} className="relative">
      {/* ── Badge Button ── */}
      <button
        type="button"
        data-testid="connectivity-badge-btn"
        onClick={() => setOpen((p) => !p)}
        className="h-8 inline-flex items-center gap-1.5 px-2.5 rounded-xl text-[10.5px] uppercase tracking-[0.14em] font-semibold transition-all"
        style={isOnline ? {
          border:     '1px solid rgba(37,181,168,0.50)',
          background: 'rgba(37,181,168,0.10)',
          color:      'rgba(94,234,212,0.90)',
        } : {
          border:     '1px solid rgba(148,163,184,0.45)',
          background: 'rgba(148,163,184,0.08)',
          color:      'rgba(203,213,225,0.85)',
        }}
      >
        {checking ? (
          <RefreshCw className="h-3 w-3 animate-spin" />
        ) : isOnline ? (
          <>
            <span className="w-1.5 h-1.5 rounded-full bg-teal-400 animate-pulse" />
            <Globe className="h-3 w-3" />
            Live
          </>
        ) : (
          <>
            <Shield className="h-3 w-3" />
            Secure Standalone
          </>
        )}
        <ChevronDown className={`h-2.5 w-2.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* ── Popover ── */}
      {open && (
        <div
          data-testid="connectivity-popover"
          className="absolute right-0 top-10 z-50 w-80 rounded-xl shadow-2xl"
          style={{
            background: 'rgb(15 32 62)',
            border:     '1px solid rgba(40,68,116,0.8)',
            boxShadow:  '0 20px 60px rgba(0,0,0,0.6)',
          }}
        >
          {/* Mode Header */}
          <div className="px-4 py-3.5 border-b border-zinc-800/60">
            <div className="flex items-center gap-2.5 mb-1">
              {isOnline
                ? <Globe className="h-4 w-4 text-teal-400" />
                : <Shield className="h-4 w-4 text-slate-400" />
              }
              <span className="text-sm font-semibold" style={{ color: isOnline ? '#5EEAD4' : '#CBD5E1' }}>
                {isOnline ? 'Online Mode — Live' : 'Enterprise Stand-Alone Mode'}
              </span>
            </div>
            <p className="text-[11px] mt-1" style={{ color: 'rgba(148,172,210,0.75)' }}>
              {isOnline
                ? 'Connected to rcd2engine.com. Real-time features, cloud backups, and AI updates are active.'
                : 'Air-gapped operation. All data stays local in SQLite. No external connections are made. Maximum security.'
              }
            </p>
            {/* Security level */}
            <div className="mt-2.5 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-[0.14em]"
              style={{
                background: isOnline ? 'rgba(37,181,168,0.1)' : 'rgba(71,85,105,0.25)',
                border:     isOnline ? '1px solid rgba(37,181,168,0.3)' : '1px solid rgba(100,116,139,0.4)',
                color:      isOnline ? 'rgba(94,234,212,0.85)' : 'rgba(203,213,225,0.75)',
              }}
            >
              <Shield className="h-2.5 w-2.5" />
              Security level: {isOnline ? 'Standard (TLS 1.3)' : 'Maximum (Air-gapped)'}
            </div>
          </div>

          {/* Status Details */}
          <div className="px-4 py-3 border-b border-zinc-800/60 space-y-1.5">
            <StatusRow label="Internet"
              ok={navigator.onLine}
              yes="Browser reports online" no="Browser reports offline" />
            <StatusRow label="R² Engine server"
              ok={serverReach === true}
              yes="rcd2engine.com reachable" no="rcd2engine.com unreachable" />
            <div className="flex items-center gap-2 text-[10px]" style={{ color: 'rgba(148,172,210,0.55)' }}>
              <span>Last probed:</span>
              <span className="font-mono">{ago}</span>
            </div>
          </div>

          {/* Feature list */}
          <div className="px-4 py-3 border-b border-zinc-800/60">
            <div className="text-[9.5px] uppercase tracking-[0.16em] text-zinc-600 mb-2">
              {isOnline ? 'Active features' : 'Active features'}
            </div>
            {(isOnline ? ONLINE_FEATURES : STANDALONE_FEATURES).map((f) => (
              <div key={f} className="flex items-center gap-1.5 text-[11px] py-0.5"
                style={{ color: 'rgba(148,172,210,0.75)' }}>
                <span className="w-1 h-1 rounded-full"
                  style={{ background: isOnline ? '#2DD4BF' : '#94A3B8' }} />
                {f}
              </div>
            ))}
          </div>

          {/* Force mode toggle */}
          <div className="px-4 py-3">
            <div className="text-[9.5px] uppercase tracking-[0.16em] text-zinc-600 mb-2">Force mode</div>
            <div className="flex gap-2">
              {(['auto', 'online', 'standalone'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  data-testid={`conn-force-${m}`}
                  onClick={() => { forceMode(m); setOpen(false); }}
                  className="flex-1 py-1.5 text-[10px] uppercase tracking-[0.14em] rounded-sm border transition"
                  style={{
                    border:     'none',
                    background: 'rgba(40,68,116,0.4)',
                    color:      'rgba(148,172,210,0.7)',
                  }}
                >
                  {m === 'auto' ? 'Auto' : m === 'online' ? 'Online' : 'Standalone'}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────────────

const ONLINE_FEATURES = [
  'Real-time AI (Emmy) via rcd2engine.com',
  'Cloud backups and sync',
  'Automatic R² personality + standards updates',
  'TIA/BICSI standard web-search',
];
const STANDALONE_FEATURES = [
  'Local SQLite (zero cloud dependency)',
  'RCDD-verified offline manuals',
  'Offline Emmy AI (configured providers only)',
  'No external network requests made',
];

function StatusRow({ label, ok, yes, no }: {
  label: string; ok: boolean; yes: string; no: string;
}): JSX.Element {
  return (
    <div className="flex items-center gap-2 text-[10px]">
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${ok ? 'bg-emerald-400' : 'bg-rose-500'}`} />
      <span style={{ color: 'rgba(148,172,210,0.7)' }}>{label}:</span>
      <span style={{ color: ok ? 'rgba(52,211,153,0.85)' : 'rgba(248,113,113,0.85)' }}>
        {ok ? yes : no}
      </span>
    </div>
  );
}
